import { createContext, useContext, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  };

  useEffect(() => {
    // Handle return from Emergent Google OAuth: session_id arrives in the URL fragment.
    const hash = window.location.hash || "";
    if (hash.includes("session_id=")) {
      const sessionId = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
      // strip the fragment so the token isn't left in the URL
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
      if (sessionId) {
        api.post("/auth/google/session", { session_id: sessionId })
          .then(({ data }) => {
            localStorage.setItem("bsp_token", data.token);
            return refresh();
          })
          .catch(() => localStorage.removeItem("bsp_token"))
          .finally(() => setLoading(false));
        return;
      }
    }
    const token = localStorage.getItem("bsp_token");
    if (!token) { setLoading(false); return; }
    refresh().catch(() => localStorage.removeItem("bsp_token")).finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("bsp_token", data.token);
      await refresh();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = () => {
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.error("Logout request failed:", e); }
    localStorage.removeItem("bsp_token");
    setUser(null);
    window.location.href = "/login";
  };

  const can = (module, action) => {
    if (!user) return false;
    if (user.is_admin) return true;
    return !!user.permissions?.[module]?.[action];
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithGoogle, logout, refresh, can, isAdmin: !!user?.is_admin }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
